import React, { useContext, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import isEmail from 'validator/lib/isEmail'
import { formatPhoneNumber } from 'utils/formUtils'
import { REGEX_PATTERNS } from 'utils/Constants'
import InputErrorMessage from 'components/generic/Input/InputErrorMessage'
import TextInput from 'components/generic/Input/TextInput'
import PrimaryButton from 'components/generic/PrimaryButton'
import CHECK_EMAIL_MUTATION from 'components/api/mutations/checkIfEmailExists.gql'
import { getHeadingForUserType } from 'utils/getStartedUtils'
import { trackClickEvent } from 'utils/Analytics'
import { LoginLink } from 'components/GetStarted/StepContactInfo/styles'
import { StepHeading, Form } from 'components/GetStarted/styles'
import { useForm } from 'react-hook-form'
import { AppContext } from 'contexts/AppContext'

const StepContactInfo = ({ data, onStepComplete, userInput }) => {
  const { domain } = useContext(AppContext)

  const { register, handleSubmit, errors, setValue, formState, getValues, trigger } = useForm({
    mode: 'all',
    defaultValues: {
      email: userInput?.email,
      phone_number: userInput?.phone_number,
    },
  })

  const [checkIfEmailExistsMutation] = useMutation(CHECK_EMAIL_MUTATION)
  const { isValid } = formState
  const onPhoneNumberChange = (e) => {
    const newValue = formatPhoneNumber(e.target.value)
    setValue('phone_number', newValue, { shouldDirty: true, shouldValidate: true })
  }
  const heading = getHeadingForUserType({
    userType: userInput?.user_type,
    providerHeading: `How can ${data?.site?.plural_suffixed_seeker_name} contact you?`,
    seekerHeading: `How can ${data?.site?.plural_provider_name} contact you?`,
    defaultHeading: `How can they contact you?`,
  })
  const isEmailAvailable = async (email) => {
    const result = await checkIfEmailExistsMutation({ variables: { email } })
    return result?.data?.check_email_available?.available ?? true
  }
  const emailValidationRules = {
    required: 'Please enter an email address.',
    validate: {
      isValid: (value) => isEmail(value) || 'Invalid email',
      isAvailable: async (value) => (await isEmailAvailable(value)) || EmailExistsErrorMessage,
    },
  }
  const phoneValidationRules = {
    required: 'Please enter a phone number.',
    pattern: {
      value: REGEX_PATTERNS.PHONE_NUMBER,
      message: 'Please enter a valid phone number. Eg. 555-555-5555',
    },
  }
  const EmailExistsErrorMessage = (
    <>
      An account with this email already exists.<LoginLink href='/log-in'>Please log in</LoginLink>
    </>
  )

  /**
   * It appears that there's a bug with react-hook-form when a field has an async
   * validation the form won't validate properly on initial load when pre-populated
   * with default values. This ensures the form triggers a validation if default values
   * exist on load. May look at opening an issue on react-hook-form github
   */
  useEffect(() => {
    const defaultValues = getValues()
    const hasDefinedValues = Object.keys(defaultValues).some(
      (key) => typeof defaultValues[key] === 'string' && defaultValues[key].length > 0
    )
    if (hasDefinedValues) {
      trigger()
    }
  }, [trigger, getValues])

  return (
    <Form onSubmit={handleSubmit(onStepComplete)}>
      <StepHeading>{heading}</StepHeading>
      <TextInput
        id='contact-info-email'
        autoFocus={true}
        name='email'
        label='Email Address'
        isValid={!errors.email}
        ref={register(emailValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='contact-info-email' name='email' />

      <TextInput
        id='contact-info-phone'
        name='phone_number'
        label='Phone Number'
        isValid={!errors.phone_number}
        onChange={onPhoneNumberChange}
        ref={register(phoneValidationRules)}
      />
      <InputErrorMessage errors={errors} inputId='contact-info-phone' name='phone_number' />
      <PrimaryButton
        mt='2rem'
        disabled={!isValid}
        onClick={() => trackClickEvent(domain, 'get-started-next-contact-info')}
      >
        Next
      </PrimaryButton>
    </Form>
  )
}

export default StepContactInfo
